import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering"; // Ruoli
import GroupsIcon from "@mui/icons-material/Groups"; // Gruppi
import GroupWorkIcon from "@mui/icons-material/GroupWork"; // Organizzazione
import PersonIcon from "@mui/icons-material/Person"; // Utenti
import baseApi from "../api/baseApi";
import { AccountTree } from "@mui/icons-material";
import { useTranslations } from "hooks";
import { getTranslation } from "common";
import NotificationGroup from "./NotificationGroup";

export interface NotificationConfig {
  id: number;
  iddescribeusersettings: string;
  active: boolean;
  name: string;
}

const NotificationsSettings: React.FC = () => {
  const CATEGORY_MAP: Record<string, { label: string; icon: JSX.Element }> = {
    "NOTIFICATION.EDIT.GROUPS": { label: 'generic_group_notifications', icon: <GroupsIcon sx={{ color: "rgba(84, 189, 193, 1)" }} /> },
    "NOTIFICATION.EDIT.ROLES": { label: "generic_notification.roles", icon: <EngineeringIcon sx={{ color: "rgba(84, 189, 193, 1)" }} /> },
    "NOTIFICATION.EDIT.USERS": { label: "generic_notification.users", icon: <PersonIcon sx={{ color: "rgba(84, 189, 193, 1)" }} /> },
    "NOTIFICATION.EDIT.ORGANIZATION": { label: "generic_notification.organization", icon: <GroupWorkIcon sx={{ color: "rgba(84, 189, 193, 1)" }} /> },
    "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU": { label: "generic_notification.activities", icon: <AccountTree sx={{ color: "rgba(84, 189, 193, 1)" }} /> }
  };
  const [notificationsConfig, setNotificationsConfig] = useState<NotificationConfig[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const translations = useTranslations()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await baseApi.get("/notifications/configuration");
        // console.log(response.data)
        setNotificationsConfig(response.data);
      } catch {
      }
    };
    fetchData();
  }, []);

  const handleActiveToggle = async (categoryPrefix: string, type: string, isActive: boolean) => {
    const relatedNotifications = notificationsConfig.filter(
      n =>
        n.iddescribeusersettings.startsWith(categoryPrefix) &&
        n.name === type
    );    

    try {
      const updatePromises = relatedNotifications.map(notification => {
        if (isActive) {
          return baseApi.delete(`/notifications/configuration/${notification.id}`);
        } else {
          return baseApi.put(`/notifications/configuration/${notification.id}`);
        }
      });

      await Promise.all(updatePromises);
      setNotificationsConfig(prev =>
        prev.map(notification => (
          relatedNotifications.some(relatedNotifications => relatedNotifications.id === notification.id)
            ? { ...notification, active: !isActive }
            : notification
        ))
      );
    } catch { }
  };

  const groupedNotifications: Record<string, NotificationConfig[]> = useMemo(() => {

    const _groupedNotifications: Record<string, NotificationConfig[]> = {};
    notificationsConfig?.forEach(notification => {
      const matchingPrefix = Object.keys(CATEGORY_MAP).find(prefix =>
        notification.iddescribeusersettings.startsWith(prefix)
      );
      if (matchingPrefix) {
        const categoryLabel = CATEGORY_MAP[matchingPrefix].label;
        if (!_groupedNotifications[categoryLabel]) {
          _groupedNotifications[categoryLabel] = [];
        }
        _groupedNotifications[categoryLabel].push(notification);
      }
    })

    return _groupedNotifications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsConfig]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <React.Fragment>
      <Typography variant="h4" sx={{ mb: 4, mt: 8, ml: 4, textAlign: "center" }}>
        {getTranslation(translations, 'generic.notifications.settings')}
      </Typography>
      <Grid container spacing={2} sx={{ borderRadius: 4, boxShadow: 4, width: "55%", ml: "auto", mr: "auto", mb: 5 }}>
        {Object.entries(groupedNotifications).map(([labelKey, notifications]) => {
          const categoryKey = Object.keys(CATEGORY_MAP).find(prefix =>
              notifications[0].iddescribeusersettings.startsWith(prefix)
            ) 
          return (
            <NotificationGroup
              key={labelKey}
              category={getTranslation(translations, labelKey)}
              categoryKey={categoryKey as string}
              expandedCategories={expandedCategories}
              handleActiveToggle={handleActiveToggle}
              notifications={notifications}
              toggleCategory={toggleCategory}
              categoryIcon={categoryKey ? CATEGORY_MAP[categoryKey].icon : ""}
            />
          );
        })}
      </Grid>
    </React.Fragment>
  );
};

export default NotificationsSettings;